import React, { useCallback, useEffect, useRef } from 'react';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import { colorFor } from '../../../utils/itemPalette';
import { ItemCard, ItemCardSkeleton } from './ItemCard';
import type { ItemRailItem } from './ItemRail';

/**
 * Horizontal paged carousel of item cards — one per page, the
 * currently-focused one centered. Bottom-of-first-screen surface.
 * Horizontal swipe cycles through the items and keeps
 * `focusedItemId` in sync with the parent.
 *
 * Each page renders {@link ItemCard}, identical to the vertical
 * rail. A dot indicator (or numeric "5 / 128" for >10 items) sits
 * under the carousel. A subtle scroll-down hint sits below the
 * indicator so the user discovers the full list below the fold.
 *
 * Sync contract:
 *   - incoming: `focusedItemId` change → programmatic scrollTo the
 *     matching page
 *   - outgoing: user swipe → `onMomentumScrollEnd` computes the
 *     landed page and fires `onFocusChange(id)`. A `programmatic`
 *     ref suppresses that callback during a programmatic scroll so
 *     we don't re-fire focus events as the carousel catches up.
 */

interface FocusedItemCarouselProps {
  items: ItemRailItem[];
  focusedItemId?: number | null;
  /** Width of the viewport (a page snaps to this minus horizontal
   *  padding). Caller measures. */
  pageWidth: number;
  /** Streaming flag drives the AWAITING MASK badge + skeleton. */
  streaming: boolean;
  onFocusChange: (id: number) => void;
  onToggleSelected: (id: number) => void;
  onEditDetails: (id: number) => void;
  onRefineBbox: (id: number) => void;
  onDeleteItem: (id: number) => void;
  onAnnotate?: (id: number) => void;
  /** #169 C1 — read-only mode for the saved-scene viewer; hides the
   *  scan-triage chips on each card, keeps select-to-share. Default
   *  false = unchanged live-scan behavior. */
  readOnly?: boolean;
  /** Optional render slot below each ItemCard. Originally used by
   *  the SerialCaptureChip (Sprint 2 / FE-9.5, retired in FE-214);
   *  left in place so future per-card extras can plug in without
   *  re-touching the carousel. */
  renderItemFooter?: (itemId: number) => React.ReactNode;
  /** #170-followup — in read-only mode, tapping a card opens its detail. */
  onItemPress?: (item: ItemRailItem['item']) => void;
}

const MAX_DOT_INDICATOR = 10;

export const FocusedItemCarousel: React.FC<FocusedItemCarouselProps> = ({
  items,
  focusedItemId,
  pageWidth,
  streaming,
  onFocusChange,
  onToggleSelected,
  onEditDetails,
  onRefineBbox,
  onDeleteItem,
  onAnnotate,
  readOnly = false,
  renderItemFooter,
  onItemPress,
}) => {
  const scrollRef = useRef<ScrollView | null>(null);
  const programmaticRef = useRef(false);
  const programmaticTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Current page index derived from focusedItemId (or 0 default).
  const focusedIndex = React.useMemo(() => {
    if (focusedItemId === null || focusedItemId === undefined) return 0;
    const idx = items.findIndex((it) => it.item.id === focusedItemId);
    return idx >= 0 ? idx : 0;
  }, [focusedItemId, items]);

  // Programmatically scroll when focusedIndex changes externally.
  useEffect(() => {
    if (!scrollRef.current) return;
    programmaticRef.current = true;
    scrollRef.current.scrollTo({
      x: focusedIndex * pageWidth,
      animated: true,
    });
    if (programmaticTimerRef.current) {
      clearTimeout(programmaticTimerRef.current);
    }
    // Momentum-end can fire after the animation completes. Clear
    // the suppression flag after a safe window so user-initiated
    // scrolls that happen to land on the same index still fire.
    programmaticTimerRef.current = setTimeout(() => {
      programmaticRef.current = false;
    }, 450);
  }, [focusedIndex, pageWidth]);

  useEffect(() => {
    return () => {
      if (programmaticTimerRef.current) {
        clearTimeout(programmaticTimerRef.current);
      }
    };
  }, []);

  const onMomentumScrollEnd = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (programmaticRef.current) return;
      const x = e.nativeEvent.contentOffset.x;
      const idx = Math.round(x / pageWidth);
      const clamped = Math.max(0, Math.min(items.length - 1, idx));
      const target = items[clamped];
      if (target?.item.id && target.item.id !== focusedItemId) {
        onFocusChange(target.item.id);
      }
    },
    [items, pageWidth, focusedItemId, onFocusChange],
  );

  const totalPages = items.length;
  const showNumeric = totalPages > MAX_DOT_INDICATOR;

  return (
    <View style={styles.wrap}>
      <ScrollView
        ref={scrollRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        directionalLockEnabled
        decelerationRate="fast"
        snapToInterval={pageWidth}
        snapToAlignment="start"
        onMomentumScrollEnd={onMomentumScrollEnd}
      >
        {items.length === 0 ? (
          <View style={[styles.page, { width: pageWidth }]}>
            {streaming ? (
              <ItemCardSkeleton />
            ) : (
              <View style={styles.emptyCard}>
                <MaterialIcons
                  name="search-off"
                  size={32}
                  color={fpColors.outline}
                />
                <Text style={styles.emptyTitle}>No items detected</Text>
                <Text style={styles.emptySubtitle}>
                  Try a different angle or better lighting and scan again.
                </Text>
              </View>
            )}
          </View>
        ) : (
          items.map((entry) => {
            const id = entry.item.id ?? -1;
            return (
              <View
                key={`carousel-${id}`}
                style={[styles.page, { width: pageWidth }]}
                accessibilityLabel={`Item ${entry.item.item_name || id}`}
              >
                <ItemCard
                  item={entry.item}
                  selected={entry.selected}
                  hasMask={entry.hasMask}
                  maskErrored={entry.maskErrored}
                  corrected={entry.corrected}
                  iou={entry.iou}
                  highlighted={entry.highlighted}
                  streaming={streaming}
                  glass
                  onToggleSelected={onToggleSelected}
                  onEditDetails={onEditDetails}
                  onRefineBbox={onRefineBbox}
                  onDeleteItem={onDeleteItem}
                  onAnnotate={onAnnotate}
                  readOnly={readOnly}
                  onItemPress={onItemPress}
                />
                {/* Optional plug-in slot below each card. Empty
                    today (SerialCaptureChip removed by FE-214). */}
                {renderItemFooter && id !== -1 && renderItemFooter(id)}
              </View>
            );
          })
        )}
      </ScrollView>

      {totalPages > 0 ? (
        <View style={styles.indicatorRow}>
          {showNumeric ? (
            <Text style={styles.numericIndicator}>
              {focusedIndex + 1} / {totalPages}
            </Text>
          ) : (
            <View style={styles.dotRow}>
              {items.map((entry, i) => {
                const id = entry.item.id ?? -1;
                const palette = colorFor(id);
                const active = i === focusedIndex;
                return (
                  <View
                    key={`dot-${id}`}
                    style={[
                      styles.dot,
                      active
                        ? { backgroundColor: palette.solid, width: 14 }
                        : { backgroundColor: 'rgba(255,255,255,0.55)' },
                    ]}
                  />
                );
              })}
            </View>
          )}
        </View>
      ) : null}

      {/* Gap #123 (2026-05-27 audit) — the "scroll for all items"
          hint used to render even when 0 items had been detected,
          which read as "there are items already scroll to find them"
          while the user was still waiting for the scan. Gate on
          items.length > 0 so the hint only appears when there's
          actually content downstream to scroll to. */}
      {items.length > 0 ? (
        <View style={styles.scrollHint} accessibilityElementsHidden>
          <MaterialIcons
            name="keyboard-arrow-down"
            size={16}
            color={fpColors.onSurfaceVariant}
          />
          <Text style={styles.scrollHintText}>scroll for all items</Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    width: '100%',
  },
  page: {
    paddingHorizontal: 16,
  },
  emptyCard: {
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderRadius: 14,
    paddingVertical: 22,
    paddingHorizontal: 18,
    alignItems: 'center',
    gap: 6,
  },
  emptyTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
    marginTop: 4,
  },
  emptySubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    lineHeight: 16,
  },
  indicatorRow: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    minHeight: 10,
  },
  dotRow: {
    flexDirection: 'row',
    gap: 5,
    alignItems: 'center',
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    ...(Platform.OS === 'web'
      ? ({
          transition: 'width 160ms ease-out, background-color 160ms',
          boxShadow: '0 1px 2px rgba(0,0,0,0.4)',
        } as any)
      : null),
  },
  numericIndicator: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: 'rgba(255,255,255,0.92)',
    letterSpacing: 0.4,
    ...(Platform.OS === 'web'
      ? ({ textShadow: '0 1px 4px rgba(0,0,0,0.6)' } as any)
      : null),
  },
  scrollHint: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    marginTop: 6,
    marginBottom: 6,
  },
  scrollHintText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 10,
    color: 'rgba(255,255,255,0.85)',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    ...(Platform.OS === 'web'
      ? ({ textShadow: '0 1px 4px rgba(0,0,0,0.6)' } as any)
      : null),
  },
});
